import React, { useEffect, useRef, useState } from "react";

import { useCallAudio } from "../../hooks/useCallAudio";
import { useCallSummary } from "../../hooks/useCallSummary";
import { useCallTranscription } from "../../hooks/useCallTranscription";
import { CallRecord, NO_USEFUL_CONTENT_SUMMARY, ProcessingStatus } from "../../types/calls.types";

import {
  DIRECTION_BADGE_CLASS,
  PROCESSING_BADGE_CLASS,
  PROCESSING_LABEL,
  PROCESSING_NOT_STARTED_BADGE_CLASS,
  PROCESSING_NOT_STARTED_LABEL,
  STATUS_BADGE_CLASS,
  STATUS_LABEL,
} from "./callsExplorer.constants";
import { RelatedCallsDimension, useCallsExplorer } from "./callsExplorerContext";
import { CallTranscript } from "./callTranscript";
import { NumberAssociations } from "./numberAssociations";

// Always UK formatting (dd/mm/yyyy, 24h) - passing `undefined` would use the
// browser's locale, which shows US dates for anyone set to en-US
const UK_LOCALE = "en-GB";
const LONDON_TZ = { timeZone: "Europe/London" } as const;

const formatDateTime = (call: CallRecord) => {
  // call_id ("<unix-seconds>.<sequence>") is the PBX channel id and doubles
  // as an unambiguous UTC call-start timestamp - prefer it over
  // call_timestamp, which is stamped with a literal "Z" even though its
  // digits are actually Europe/Madrid local time, not UTC.
  const epochSeconds = parseFloat(call.call_id);
  if (!Number.isNaN(epochSeconds)) {
    const dt = new Date(epochSeconds * 1000);
    return {
      date: dt.toLocaleDateString(UK_LOCALE, LONDON_TZ),
      time: dt.toLocaleTimeString(UK_LOCALE, { hour: "2-digit", minute: "2-digit", ...LONDON_TZ }),
    };
  }

  // Fallback if call_id is ever missing/unparseable: derive from
  // call_timestamp, correcting for the mislabeled "Z".
  // Spain is always exactly 1 hour ahead of the UK (both follow the same
  // EU-aligned DST transition dates), so a fixed shift works without needing
  // to know Madrid's actual UTC offset on any given date.
  const raw = call.call_timestamp;
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/.exec(raw);
  if (!match) return { date: "—", time: "—" };
  const [, y, mo, d, h, mi, s] = match.map(Number);
  const dt = new Date(Date.UTC(y, mo - 1, d, h, mi, s) - 60 * 60 * 1000);
  return {
    date: dt.toLocaleDateString(UK_LOCALE, { timeZone: "UTC" }),
    time: dt.toLocaleTimeString(UK_LOCALE, { hour: "2-digit", minute: "2-digit", timeZone: "UTC" }),
  };
};

const formatDuration = (seconds: number) => {
  if (!seconds) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString()}:${secs.toString().padStart(2, "0")}`;
};

// The API doesn't always send call_cost as a number (decimal string / null),
// despite what the type says, so coerce before formatting
const formatCost = (cost: number | string | null) => `£${Number(cost ?? 0).toFixed(5)}`;

// AUDIO_LINKED_PROVISIONAL is functionally the same as AUDIO_LINKED - audio
// is available for both, the only difference is which bucket the record
// currently lives in.
const hasAudio = (status: CallRecord["status"]) =>
  status === "AUDIO_LINKED" || status === "AUDIO_LINKED_PROVISIONAL";

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="col-md-3 col-6 mb-2">
    <div className="text-muted small">{label}</div>
    <div className="fw-bold">{value ?? "—"}</div>
  </div>
);

const LoadingMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-muted small fw-bold d-flex align-items-center">
    <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
    {children}
  </div>
);

const StatusBadge: React.FC<{ letter: string; className: string; title: string }> = ({
  letter,
  className,
  title,
}) => (
  <span className={`badge ${className} me-1`} title={title} style={{ width: "1.6rem" }}>
    {letter}
  </span>
);

// A / T / S - audio, transcript and summary progress at a glance
const CallStatusBadges: React.FC<{ call: CallRecord }> = ({ call }) => {
  const processingBadge = (letter: string, label: string, status: ProcessingStatus | null) => (
    <StatusBadge
      letter={letter}
      className={status ? PROCESSING_BADGE_CLASS[status] : PROCESSING_NOT_STARTED_BADGE_CLASS}
      title={`${label}: ${status ? PROCESSING_LABEL[status] : PROCESSING_NOT_STARTED_LABEL}`}
    />
  );

  return (
    <span className="text-nowrap">
      <StatusBadge
        letter="A"
        className={STATUS_BADGE_CLASS[call.status]}
        title={`Audio: ${STATUS_LABEL[call.status]}`}
      />
      {processingBadge("T", "Transcript", call.transcription_status)}
      {processingBadge("S", "Summary", call.summary_status)}
    </span>
  );
};

interface CallRowProps {
  call: CallRecord;
  isExpanded: boolean;
  onExpand: (callId: string) => void;
  canPlayAudio: boolean;
  canViewTranscript: boolean;
}

export const CallRow: React.FC<CallRowProps> = ({
  call,
  isExpanded,
  onExpand,
  canPlayAudio,
  canViewTranscript,
}) => {
  const { loadAudio, loading, error, audioUrl } = useCallAudio();
  const transcript = useCallTranscription();
  const summary = useCallSummary();
  const audioRef = useRef<HTMLAudioElement>(null);
  const frameRef = useRef<number | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const { openRelatedCalls } = useCallsExplorer();
  const { date, time } = formatDateTime(call);
  const callingName = call.calling_name;

  // For inbound calls, called_number is just the internal extension that was
  // rung - the number that actually matters is who called in (caller_id).
  // For outbound/internal calls, called_number is the meaningful one.
  const customerNumberDimension: RelatedCallsDimension =
    call.direction === "inbound" ? "caller_id" : "called_number";
  const customerNumber =
    call.direction === "inbound"
      ? (call.caller_id_normalised ?? call.caller_id)
      : (call.called_number_normalised ?? call.called_number);

  useEffect(() => {
    if (!isExpanded || !hasAudio(call.status)) return;
    if (canPlayAudio) {
      void loadAudio(call.call_id);
      // Fetched alongside the recording so it's ready by the time it's played
      if (canViewTranscript) void transcript.loadTranscription(call.call_id);
    }
    // Summaries share the transcript permission, but don't need the player
    if (canViewTranscript) void summary.loadSummary(call.call_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  // `timeupdate` only fires ~4x a second - too coarse for word-level
  // highlighting - so poll the playhead every frame while playing.
  const stopTracking = () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  };

  const syncTime = () => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
  };

  const startTracking = () => {
    stopTracking();
    const tick = () => {
      syncTime();
      frameRef.current = requestAnimationFrame(tick);
    };
    tick();
  };

  // Collapsing the row unmounts the player without a pause event
  useEffect(() => stopTracking, []);

  const retryButton = (onRetry: (callId: string) => Promise<void>) => (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary ms-2 flex-shrink-0"
      onClick={() => {
        void onRetry(call.call_id);
      }}
    >
      Check again
    </button>
  );

  const seekTo = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = seconds;
    setCurrentTime(seconds);
    void audio.play();
  };

  return (
    <React.Fragment>
      <tr
        onClick={() => {
          onExpand(call.call_id);
        }}
        style={{ cursor: "pointer" }}
      >
        <td>
          <div className="small">
            <div className="fw-bold">{date}</div>
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
              {time}
            </div>
          </div>
        </td>
        <td>
          <CallStatusBadges call={call} />
        </td>
        <td>
          {call.direction ? (
            <span className={`badge ${DIRECTION_BADGE_CLASS[call.direction]}`}>
              {call.direction}
            </span>
          ) : (
            "—"
          )}
        </td>
        <td>
          {callingName ? (
            <button
              type="button"
              className="badge rounded-pill bg-light text-dark border fw-normal px-3 py-2"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                openRelatedCalls("calling_name", callingName, callingName);
              }}
            >
              {callingName}
            </button>
          ) : (
            "—"
          )}
        </td>
        <td>
          {customerNumber ? (
            <button
              type="button"
              className="badge rounded-pill bg-light text-dark border fw-normal px-3 py-2"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                openRelatedCalls(customerNumberDimension, customerNumber, customerNumber);
              }}
            >
              {customerNumber}
            </button>
          ) : (
            "—"
          )}
        </td>
        <td>{formatDuration(call.duration)}</td>
        <td className="text-end">
          <button
            className="btn btn-sm btn-primary fw-bold"
            onClick={(e) => {
              e.stopPropagation();
              onExpand(call.call_id);
            }}
          >
            {isExpanded ? "Collapse" : "Details"}
          </button>
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td colSpan={7} className="bg-white p-4 border-start border-4 border-primary">
            <div className="row">
              <DetailField label="Call ID" value={call.call_id} />
              <DetailField label="Tenant" value={call.tenant} />
              <DetailField label="Extension" value={call.extension} />
              <DetailField label="Caller ID" value={call.caller_id_normalised ?? call.caller_id} />
              <DetailField label="Called number (raw)" value={call.called_number} />
              <DetailField label="Duration" value={formatDuration(call.duration)} />
              <DetailField
                label="Billed duration"
                value={formatDuration(call.call_rating_duration)}
              />
              <DetailField label="Call cost" value={formatCost(call.call_cost)} />
              <DetailField
                label="Call ended"
                value={
                  call.call_end_time
                    ? new Date(call.call_end_time).toLocaleString(UK_LOCALE, LONDON_TZ)
                    : null
                }
              />
              <DetailField
                label="Processed at"
                value={new Date(call.processed_at).toLocaleString(UK_LOCALE, LONDON_TZ)}
              />
            </div>

            {call.direction === "outbound" && call.called_number && (
              <NumberAssociations rawNumber={call.called_number} />
            )}

            {hasAudio(call.status) && (
              <div className="mt-3">
                {canViewTranscript && (
                  <div className="mb-3">
                    <h6 className="fw-bold">Summary</h6>
                    {summary.loading ? (
                      <LoadingMessage>Loading summary...</LoadingMessage>
                    ) : summary.error ? (
                      <div className="alert alert-danger py-2 mb-0 d-flex align-items-center justify-content-between">
                        {summary.error}
                        {retryButton(summary.loadSummary)}
                      </div>
                    ) : summary.unavailableReason ? (
                      <div className="text-muted small d-flex align-items-center justify-content-between">
                        <span>
                          {summary.unavailableReason} Summaries follow shortly after the transcript.
                        </span>
                        {retryButton(summary.loadSummary)}
                      </div>
                    ) : summary.summary?.trim() === NO_USEFUL_CONTENT_SUMMARY ? (
                      <div className="text-muted small fst-italic">
                        Nothing to summarise - e.g. a voicemail, missed or dropped call.
                      </div>
                    ) : summary.summary ? (
                      <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                        {summary.summary}
                      </p>
                    ) : null}
                  </div>
                )}

                <h6 className="fw-bold">Recording</h6>
                {!canPlayAudio ? (
                  <div className="alert alert-secondary py-2 mb-0">
                    You don&apos;t have permission to play recordings.
                  </div>
                ) : loading ? (
                  <LoadingMessage>Loading audio...</LoadingMessage>
                ) : error ? (
                  <div className="alert alert-danger py-2 mb-0">{error}</div>
                ) : audioUrl ? (
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    controls
                    className="w-100"
                    onPlay={startTracking}
                    onPause={stopTracking}
                    onEnded={stopTracking}
                    onSeeked={syncTime}
                  >
                    {/* The transcript below (when available) serves as the captions */}
                    <track kind="captions" />
                  </audio>
                ) : null}

                {canPlayAudio && canViewTranscript && (
                  <div className="mt-3">
                    <h6 className="fw-bold">Transcript (click a word to skip to it)</h6>
                    {transcript.loading ? (
                      <LoadingMessage>Loading transcription...</LoadingMessage>
                    ) : transcript.error ? (
                      <div className="alert alert-danger py-2 mb-0 d-flex align-items-center justify-content-between">
                        {transcript.error}
                        {retryButton(transcript.loadTranscription)}
                      </div>
                    ) : transcript.unavailableReason ? (
                      <div className="text-muted small d-flex align-items-center justify-content-between">
                        <span>
                          {transcript.unavailableReason} Recent calls can take a minute or two to
                          transcribe.
                        </span>
                        {retryButton(transcript.loadTranscription)}
                      </div>
                    ) : transcript.segments ? (
                      <CallTranscript
                        segments={transcript.segments}
                        currentTime={currentTime}
                        onSeek={seekTo}
                      />
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};
